import {Component, lazy, Suspense, useEffect} from 'react';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import {captureException, getIsSendTransactionErrorUnexpected} from 'r/utils';
import {UiError} from 'r/ui';
import Providers from './Providers';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import App from './App';
import {createPortal} from 'react-dom';
import {ToastContainer} from 'react-toastify';
import {WagmiProvider} from 'wagmi';
import {wagmiAdapter} from 'r/utils/wagmiConfig';
import {useHydrateAtoms} from 'jotai/react/utils';
import {queryClientAtom} from 'jotai-tanstack-query';
import {useLocation} from 'react-router-dom';

const queryClient = new QueryClient();

function ScrollToTop() {
  const {pathname} = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function HydrateQueryClient({children}) {
  useHydrateAtoms([[queryClientAtom, queryClient]]);
  return children;
}

const ReactQueryDevtoolsProduction = lazy(() =>
  import(
    /* webpackChunkName: "tanstack" */ '@tanstack/react-query-devtools/build/modern/production.js'
  ).then(d => ({
    default: d.ReactQueryDevtools,
  })),
);

export default class Root extends Component {
  state = {
    globalUiError: false,
    isTanstackShown: false,
  };

  componentDidMount() {
    // No-op - toggleTanstack is no longer exposed globally
    // State is managed internally by the component
  }

  componentWillUnmount() {
    // Clean up the window property to prevent memory leaks
    delete window.toggleTanstack;
  }

  componentDidCatch(error, info) {
    if (!getIsSendTransactionErrorUnexpected(error)) {
      return;
    }
    let extra = {info};
    if (error?.fromFetchApi) {
      extra = {...extra, ...error.extra};
    }
    extra.type = 'ErrorPage';
    captureException(error, extra);
    this.setState({globalUiError: true});
  }

  render() {
    const {globalUiError, isTanstackShown} = this.state;

    if (globalUiError) {
      return <UiError/>;
    }

    return (
      <WagmiProvider config={wagmiAdapter.wagmiConfig}>
        <QueryClientProvider client={queryClient}>
          <HydrateQueryClient>
            <ScrollToTop/>
            {createPortal(<ToastContainer/>, document.body)}
            <Providers>
              <App/>
            </Providers>
            <ReactQueryDevtools/>
            {isTanstackShown && (
              <Suspense fallback={null}>
                <ReactQueryDevtoolsProduction/>
              </Suspense>
            )}
          </HydrateQueryClient>
        </QueryClientProvider>
      </WagmiProvider>
    );
  }
}
